/**
 * Enhanced Questionnaire Data Loader
 * Converts the enhanced questionnaire JSON structure into TypeScript format
 * Handles all 308 questions with dynamic branching logic
 */

import { Section, Question } from '../types/questionnaire';

// Load and parse the enhanced questionnaire JSON
// NOTE: This function is kept for backward compatibility but should use React Query hook
// Use useLoadQuestionnaire() hook instead in components
const loadEnhancedQuestionnaire = async (country: 'USA' | 'ES' = 'USA'): Promise<Section[]> => {
  try {
    // Determine file path based on country
    const fileName = country === 'USA' 
      ? '/USA_Tax_Questionnaire_Enhanced_JSON.json'
      : '/SPAIN_Tax_Questionnaire_Decision_Tree_Enhanced.json';
    
    // Try to load from public folder
    // eslint-disable-next-line no-restricted-syntax
    const response = await fetch(fileName);
    if (!response.ok) {
      throw new Error(`Failed to load questionnaire: ${response.statusText}`);
    }
    const data = await response.json();
    return convertJSONToSections(data, country);
  } catch (error) {
    console.error(`Error loading ${country} questionnaire JSON, using inline data:`, error);
    return getInlineQuestionnaireData();
  }
};

/**
 * GENERIC: Flatten questionnaire questions with nested branches into a flat array
 * with proper conditional logic attached to each nested question.
 * 
 * This handles any questionnaire structure that uses nested `branches` objects:
 * {
 *   "id": "Q1",
 *   "question": "...",
 *   "branches": {
 *     "Option A": [{ "id": "Q2", ... }],
 *     "Option B": [{ "id": "Q3", ... }]
 *   }
 * }
 * 
 * The nested questions get extracted and given conditional logic:
 * { "id": "Q2", "conditional": { "dependsOn": "Q1", "showWhen": "Option A" } }
 * 
 * Works for USA, Spain, or any country questionnaire with this structure.
 */
const flattenNestedBranches = (questions: any[]): any[] => {
  const result: any[] = [];
  
  const processQuestion = (q: any, parentId?: string, branchValue?: string) => {
    // Clone the question to avoid mutation
    const question = { ...q };
    
    // If this question came from a branch, add conditional logic
    // Don't overwrite existing conditional if present
    if (parentId && branchValue && !question.conditional) {
      question.conditional = {
        dependsOn: parentId,
        showWhen: branchValue
      };
    }
    
    // Add the question to results (without the branches property to avoid circular refs)
    const { branches, ...questionWithoutBranches } = question;
    result.push(questionWithoutBranches);
    
    // Process nested branches recursively
    if (branches && typeof branches === 'object') {
      Object.entries(branches).forEach(([branchKey, branchQuestions]) => {
        if (Array.isArray(branchQuestions)) {
          branchQuestions.forEach((nestedQ: any) => {
            processQuestion(nestedQ, q.id, branchKey);
          });
        }
      });
    }
  };
  
  // Process all top-level questions
  questions.forEach(q => processQuestion(q));
  
  return result;
};

/**
 * GENERIC: Detect if a questions array uses nested branches structure.
 * Returns true if any question has a 'branches' property with nested questions.
 */
const hasNestedBranches = (questions: any[]): boolean => {
  return questions.some(q => 
    q.branches && 
    typeof q.branches === 'object' && 
    Object.values(q.branches).some(b => Array.isArray(b) && b.length > 0)
  );
};

// Export convertJSONToSections for use with React Query
// Handles both USA and Spain questionnaire structures
export const convertJSONToSections = (jsonData: any, country: 'USA' | 'ES' = 'USA'): Section[] => {
  const sections: Section[] = [];

  if (country === 'USA') {
    // USA structure: usa_tax_questionnaire_enhanced.sections
    const sectionsData = jsonData.usa_tax_questionnaire_enhanced?.sections || {};

    Object.keys(sectionsData).forEach((sectionKey) => {
      const sectionData = sectionsData[sectionKey];
      const rawQuestions = sectionData.questions || [];
      
      // GENERIC: If questions have nested branches, flatten them
      // This works for any questionnaire structure with nested branches
      const flattenedQuestions = hasNestedBranches(rawQuestions) 
        ? flattenNestedBranches(rawQuestions) 
        : rawQuestions;
      
      const questions: Question[] = convertQuestions(flattenedQuestions, country);
      
      sections.push({
        id: sectionKey,
        title: sectionData.title || sectionKey,
        description: sectionData.description,
        order: sectionData.section_id || 0,
        required: true,
        questions: questions
      });
    });
  } else if (country === 'ES') {
    // Spain structure: questionnaire_decision_tree.parts
    const parts = jsonData.questionnaire_decision_tree?.parts || {};
    
    // FIRST PASS: Collect ALL questions from ALL parts to build global conditional_next map
    const allQuestions: Array<{ question: any; partKey: string; questionIndex: number }> = [];
    
    Object.keys(parts).forEach((partKey) => {
      const partData = parts[partKey];
      const questions = partData.questions || [];
      
      questions.forEach((q: any, index: number) => {
        allQuestions.push({
          question: q,
          partKey,
          questionIndex: index
        });
      });
    });
    
    // Build global conditional_next map and question index
    const globalConditionalNextMap = new Map<string, Map<string, string>>();
    const globalQuestionIdToIndex = new Map<string, number>();
    
    allQuestions.forEach((item, globalIndex) => {
      const q = item.question;
      globalQuestionIdToIndex.set(q.id, globalIndex);
      
      if (q.conditional_next && typeof q.conditional_next === 'object') {
        const mapping = new Map<string, string>();
        Object.entries(q.conditional_next).forEach(([answer, nextQuestionId]: [string, any]) => {
          if (typeof nextQuestionId === 'string' && 
              nextQuestionId !== 'continue_to_next_income_type' && 
              nextQuestionId !== 'SUMMARY') {
            mapping.set(answer, nextQuestionId);
          }
        });
        if (mapping.size > 0) {
          globalConditionalNextMap.set(q.id, mapping);
        }
      }
    });
    
    // Build reverse mapping: for each question, find which parent questions lead to it
    const globalQuestionConditionals = new Map<string, Array<{ dependsOn: string; showWhen: string | string[] }>>();
    
    globalConditionalNextMap.forEach((mapping, parentQuestionId) => {
      mapping.forEach((targetQuestionId, answerValue) => {
        if (!globalQuestionConditionals.has(targetQuestionId)) {
          globalQuestionConditionals.set(targetQuestionId, []);
        }
        const conditionals = globalQuestionConditionals.get(targetQuestionId)!;
        const existing = conditionals.find(c => c.dependsOn === parentQuestionId);
        if (existing) {
          if (Array.isArray(existing.showWhen)) {
            if (!existing.showWhen.includes(answerValue)) {
              existing.showWhen.push(answerValue);
            }
          } else {
            if (existing.showWhen !== answerValue) {
              existing.showWhen = [existing.showWhen, answerValue];
            }
          }
        } else {
          conditionals.push({ dependsOn: parentQuestionId, showWhen: answerValue });
        }
      });
    });
    
    // Propagate conditionals: questions that depend on a conditional question should inherit the same conditional logic
    // Find all questions that are targets of conditional_next and propagate to subsequent questions
    globalQuestionConditionals.forEach((conditionals, questionId) => {
      const questionGlobalIndex = globalQuestionIdToIndex.get(questionId);
      if (questionGlobalIndex === undefined) return;
      
      // Find the next break point (a question that is a target of conditional_next or has its own conditional_next)
      let nextBreakIndex = allQuestions.length;
      for (let i = questionGlobalIndex + 1; i < allQuestions.length; i++) {
        const nextQ = allQuestions[i].question;
        let isBreakPoint = false;
        
        // Check if this question is a target of any conditional_next
        if (globalQuestionConditionals.has(nextQ.id)) {
          // Check if it's a different conditional (different parent)
          const nextConditionals = globalQuestionConditionals.get(nextQ.id)!;
          const currentConditional = conditionals[0];
          if (currentConditional && nextConditionals.some(c => c.dependsOn !== currentConditional.dependsOn)) {
            isBreakPoint = true;
          }
        }
        
        // Check if this question has its own conditional_next
        if (globalConditionalNextMap.has(nextQ.id)) {
          isBreakPoint = true;
        }
        
        // Check if this question is referenced as a target with a different answer value
        globalConditionalNextMap.forEach((mapping) => {
          mapping.forEach((targetId, answerValue) => {
            if (targetId === nextQ.id && questionId !== nextQ.id) {
              isBreakPoint = true;
            }
          });
        });
        
        if (isBreakPoint) {
          nextBreakIndex = i;
          break;
        }
      }
      
      // Apply the same conditional to all questions between current and break point
      for (let i = questionGlobalIndex + 1; i < nextBreakIndex; i++) {
        const subsequentQ = allQuestions[i].question;
        if (!globalQuestionConditionals.has(subsequentQ.id)) {
          globalQuestionConditionals.set(subsequentQ.id, [...conditionals]);
        }
      }
    });
    
    // SECOND PASS: Convert each part's questions with global conditional context
    let order = 1;
    Object.keys(parts).forEach((partKey) => {
      const partData = parts[partKey];
      const rawQuestions = partData.questions || [];
      
      // GENERIC: If questions have nested branches, flatten them
      // This allows Spain or any other country to use nested branches if needed
      const flattenedQuestions = hasNestedBranches(rawQuestions) 
        ? flattenNestedBranches(rawQuestions) 
        : rawQuestions;
      
      const questions: Question[] = convertQuestions(
        flattenedQuestions, 
        country,
        globalQuestionConditionals
      );
      
      sections.push({
        id: partKey,
        title: partData.name || partKey,
        description: partData.description,
        order: order++,
        required: true,
        questions: questions
      });
    });
  }

  return sections.sort((a, b) => a.order - b.order);
};

// Convert JSON questions to TypeScript Question format
// Handles both USA and Spain question structures
const convertQuestions = (
  questionsData: any[], 
  country: 'USA' | 'ES' = 'USA',
  globalQuestionConditionals?: Map<string, Array<{ dependsOn: string; showWhen: string | string[] }>>
): Question[] => {
  // First pass: collect Q6 options for reference
  const autonomousCommunities = [
    'Andalucía',
    'Aragón',
    'Asturias (Principado de Asturias)',
    'Baleares (Islas Baleares)',
    'Canarias (Islas Canarias)',
    'Cantabria',
    'Castilla-La Mancha',
    'Castilla y León',
    'Cataluña (Catalonia)',
    'Extremadura',
    'Galicia',
    'Madrid (Comunidad de Madrid)',
    'Murcia (Región de Murcia)',
    'La Rioja',
    'Valencia (Comunidad Valenciana)',
    'País Vasco (Basque Country)',
    'Navarra (Navarre)'
  ];
  
  // Find Q6 options if available
  let q6Options: string[] = autonomousCommunities; // Default fallback
  if (country === 'ES') {
    const q6Question = questionsData.find((q: any) => q.id === 'Q6');
    if (q6Question && Array.isArray(q6Question.options)) {
      q6Options = q6Question.options;
    }
  }

  return questionsData.map((q: any) => {
    // Handle different field names between USA and Spain
    const questionText = country === 'ES' ? q.question_text : q.question;
    const questionType = country === 'ES' ? q.field_type : q.type;
    
    // Ensure options is always an array
    let questionOptions: string[] = [];
    if (country === 'ES') {
      const rawOptions = q.options || q.options_resident || q.options_non_resident;
      if (rawOptions === 'dynamic_country_list' || rawOptions === 'countries_with_dta_spain') {
        // Replace dynamic country list with actual countries that have DTA with Spain
        questionOptions = [
          'United States', 'United Kingdom', 'France', 'Germany', 'Italy',
          'Portugal', 'Netherlands', 'Belgium', 'Switzerland', 'Luxembourg',
          'Austria', 'Ireland', 'Denmark', 'Sweden', 'Norway', 'Finland',
          'Poland', 'Czech Republic', 'Hungary', 'Romania', 'Bulgaria',
          'Croatia', 'Slovenia', 'Slovakia', 'Estonia', 'Latvia', 'Lithuania',
          'Greece', 'Cyprus', 'Malta', 'Iceland', 'Liechtenstein', 'Monaco',
          'Andorra', 'San Marino', 'Vatican City', 'Japan', 'South Korea',
          'China', 'India', 'Australia', 'New Zealand', 'Canada', 'Mexico',
          'Brazil', 'Argentina', 'Chile', 'Colombia', 'Peru', 'Venezuela',
          'Ecuador', 'Uruguay', 'Paraguay', 'Bolivia', 'Panama', 'Costa Rica',
          'Dominican Republic', 'Morocco', 'Algeria', 'Tunisia', 'Egypt',
          'South Africa', 'Israel', 'Turkey', 'Russia', 'Ukraine', 'Other'
        ];
      } else if (rawOptions === 'same_as_Q6') {
        // Use Q6's options (Autonomous Communities)
        questionOptions = q6Options;
      } else if (rawOptions === 'dynamic_dependent_checkboxes') {
        // This will be handled dynamically in the component based on number of dependents
        // For now, set empty array - component will generate checkboxes
        questionOptions = [];
      } else {
        questionOptions = Array.isArray(rawOptions) ? rawOptions : [];
      }
    } else {
      const rawOptions = q.options;
      questionOptions = Array.isArray(rawOptions) ? rawOptions : [];
    }

    // Convert Spain conditional_next to conditional format
    // Note: Spain uses conditional_next which maps answer values to next question IDs
    // We convert this to conditional logic by checking if this question is referenced
    // in any conditional_next mapping
    let conditional = q.conditional ? convertConditional(q.conditional) : undefined;
    
    // For Spain, check if this question has conditional logic from conditional_next
    // Use the global conditionals map that was built across all sections
    // BUT: Don't overwrite if we already have a numeric conditional (preserve conditionType/minValue)
    if (country === 'ES' && globalQuestionConditionals && globalQuestionConditionals.has(q.id)) {
      const conditionals = globalQuestionConditionals.get(q.id)!;
      // Use the first conditional (most common case is one parent question)
      // If there are multiple conditionals from different parents, we'll use the first one
      // In most cases, a question will only have one conditional dependency
      const firstConditional = conditionals[0];
      if (firstConditional && !conditional?.conditionType) {
        // Only overwrite if we don't already have a numeric conditional
        conditional = {
          dependsOn: firstConditional.dependsOn,
          showWhen: firstConditional.showWhen
        };
      }
    }

    // Handle year field type - convert to select with year options
    let finalType = convertQuestionType(questionType);
    let finalOptions = questionOptions;
    
    if (questionType === 'year' || q.field_type === 'year') {
      finalType = 'select';
      // Generate year options (current year back to 20 years ago)
      const currentYear = new Date().getFullYear();
      const years: string[] = [];
      for (let i = 0; i <= 20; i++) {
        years.push((currentYear - i).toString());
      }
      finalOptions = years;
    }
    
    // Determine step value: currency fields should use 0.01 for decimal precision
    let stepValue = q.step;
    if (!stepValue && (q.field_type === 'currency' || finalType === 'number')) {
      // If it's a currency field or number field without explicit step, check if it's currency
      if (q.field_type === 'currency' || q.currency || questionText.toLowerCase().includes('currency') || questionText.toLowerCase().includes('€') || questionText.toLowerCase().includes('eur')) {
        stepValue = 0.01; // Currency fields need decimal precision
      }
    }
    
    return {
      id: q.id,
      question: questionText,
      type: finalType,
      required: q.required || false,
      options: finalOptions,
      validation: convertValidation(q.validation),
      conditional: conditional,
      helpText: q.help_text || q.description || q.purpose,
      placeholder: q.placeholder,
      min: q.min,
      max: q.max,
      step: stepValue,
      repeatable: q.repeatable || false,
      // For multi-input type, add inputType and dependsOn
      inputType: q.input_type || (q.field_type === 'multi-input' ? 'number' : undefined),
      // For multi-input fields, use conditional.dependsOn to determine the count
      // This is driven entirely by the decision tree JSON, no hardcoded values
      dependsOn: (q.field_type === 'multi-input' || q.options === 'dynamic_dependent_checkboxes') 
        ? (q.conditional?.dependsOn || undefined)
        : undefined,
      // For calculated fields, preserve calculation_logic from JSON (snake_case -> camelCase)
      // Source: JSON field "calculation_logic" contains formula, display, description, etc.
      calculationLogic: q.calculation_logic,
      // Mark as read-only if it's a calculated field
      readOnly: q.read_only || q.field_type === 'calculated' || false,
      // Inline validation properties for pattern-based validation
      pattern: q.pattern,
      mask: q.mask,
      error_message: q.error_message,
      sensitive: q.sensitive,
      min_length: q.min_length,
      max_length: q.max_length
    };
  });
};

// Convert question type (handles both USA and Spain field types)
const convertQuestionType = (type: string): Question['type'] => {
  const typeMap: { [key: string]: Question['type'] } = {
    'radio': 'radio',
    'checkbox': 'checkbox',
    'text': 'text',
    'number': 'number',
    'date': 'date',
    'select': 'select',
    'multiselect': 'multiselect',
    'multi-select': 'multiselect', // Spain uses multi-select
    'multi-input': 'multi-input', // For dynamic input fields (e.g., dependent ages)
    'textarea': 'textarea',
    'currency': 'number', // Currency fields should be numeric input (not alphanumeric)
    'calculated': 'calculated', // Spain calculated fields - keep as calculated type
    'calculated_radio': 'radio', // Spain calculated radio fields should be radio buttons
    'yes_no': 'radio', // Yes/No questions should be radio buttons
    'boolean': 'radio', // Boolean fields should be radio buttons
    'year': 'select' // Year fields should be select dropdowns (handled separately with year options)
  };
  return typeMap[type] || 'text';
};

// Convert validation rules
const convertValidation = (validation: any): any[] => {
  if (!validation) return [];
  
  if (typeof validation === 'string') {
    // Handle string-based validation types
    return [{
      type: validation,
      message: getValidationMessage(validation)
    }];
  }
  
  if (Array.isArray(validation)) {
    return validation;
  }
  
  return [];
};

// Get validation message for string-based validation
const getValidationMessage = (validationType: string): string => {
  const messages: { [key: string]: string } = {
    'not_empty': 'This field is required',
    'ssn_or_itin_format': 'Please enter a valid SSN (XXX-XX-XXXX) or ITIN',
    'valid_date': 'Please enter a valid date',
    'zip_code_format': 'Please enter a valid ZIP code',
    'ip_pin_format': 'IP PIN must be 6 digits',
    'between_0_and_365': 'Must be between 0 and 365',
    'positive_integer': 'Must be a positive integer'
  };
  return messages[validationType] || 'Invalid input';
};

// Convert conditional logic
const convertConditional = (conditional: any): any => {
  if (!conditional?.dependsOn) {
    return undefined;
  }
  
  // Handle numeric conditions (even without showWhen)
  if (conditional?.conditionType === 'numeric' && conditional?.minValue !== undefined) {
    return {
      dependsOn: conditional.dependsOn,
      conditionType: conditional.conditionType,
      minValue: conditional.minValue,
      maxValue: conditional.maxValue,
      showWhen: conditional.showWhen,
      hideWhen: conditional.hideWhen
    };
  }
  
  // Handle standard conditions with showWhen
  if (conditional?.showWhen !== undefined) {
    return {
      dependsOn: conditional.dependsOn,
      showWhen: conditional.showWhen,
      hideWhen: conditional.hideWhen,
      conditionType: conditional.conditionType,
      minValue: conditional.minValue,
      maxValue: conditional.maxValue
    };
  }
  
  return undefined;
};

// Export getInlineQuestionnaireData for fallback use
export const getInlineQuestionnaireData = (): Section[] => {
  // This would contain the full questionnaire data
  // For now, return a basic structure that matches the enhanced questionnaire
  return [
    {
      id: 'personal_identity',
      title: 'Personal Identity & Basic Information',
      description: 'Please provide your basic personal information as it appears on official documents.',
      order: 1,
      required: true,
      questions: [
        {
          id: 'Q1',
          question: 'What is your full legal name (as it appears on your Social Security card)?',
          type: 'text',
          required: true,
          validation: [
            { type: 'required', message: 'Full name is required' },
            { type: 'pattern', value: /^[a-zA-Z\s'-]+$/, message: 'Name can only contain letters, spaces, hyphens, and apostrophes' }
          ],
          placeholder: 'Enter your full legal name'
        },
        {
          id: 'Q2',
          question: 'What is your Social Security Number (SSN) or Individual Taxpayer Identification Number (ITIN)?',
          type: 'text',
          required: true,
          validation: [
            { type: 'required', message: 'SSN or ITIN is required' },
            { type: 'pattern', value: /^\d{3}-\d{2}-\d{4}$|^\d{9}$|^9\d{2}-\d{2}-\d{4}$/, message: 'Please enter a valid SSN (XXX-XX-XXXX) or ITIN (9XX-XX-XXXX)' }
          ],
          placeholder: 'XXX-XX-XXXX or 9XX-XX-XXXX'
        },
        {
          id: 'Q3',
          question: 'What is your date of birth?',
          type: 'date',
          required: true,
          validation: [
            { type: 'required', message: 'Date of birth is required' }
          ]
        },
        {
          id: 'Q4',
          question: 'What is your current mailing address?',
          type: 'textarea',
          required: true,
          validation: [
            { type: 'required', message: 'Mailing address is required' }
          ],
          placeholder: 'Enter your complete mailing address'
        },
        {
          id: 'Q5',
          question: 'Is this a U.S. address?',
          type: 'radio',
          required: true,
          options: ['Yes', 'No'],
          conditional: {
            dependsOn: 'Q4',
            showWhen: true
          }
        },
        {
          id: 'Q6',
          question: 'What is your state of residence?',
          type: 'select',
          required: true,
          options: [
            'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 
            'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 
            'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 
            'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 
            'WI', 'WY', 'DC'
          ],
          conditional: {
            dependsOn: 'Q5',
            showWhen: 'Yes'
          }
        },
        {
          id: 'Q7',
          question: 'What is your ZIP code?',
          type: 'text',
          required: true,
          validation: [
            { type: 'required', message: 'ZIP code is required' },
            { type: 'pattern', value: /^\d{5}(-\d{4})?$/, message: 'Please enter a valid ZIP code' }
          ],
          placeholder: '12345 or 12345-6789',
          conditional: {
            dependsOn: 'Q5',
            showWhen: 'Yes'
          }
        },
        {
          id: 'Q8',
          question: 'What is your foreign address (street, city, province/state, postal code, country)?',
          type: 'textarea',
          required: true,
          validation: [
            { type: 'required', message: 'Foreign address is required' }
          ],
          placeholder: 'Enter your complete foreign address',
          conditional: {
            dependsOn: 'Q5',
            showWhen: 'No'
          }
        },
        {
          id: 'Q9',
          question: 'Do you maintain a U.S. address for tax purposes?',
          type: 'radio',
          required: true,
          options: ['Yes', 'No'],
          conditional: {
            dependsOn: 'Q5',
            showWhen: 'No'
          }
        },
        {
          id: 'Q10',
          question: 'What is your U.S. address?',
          type: 'textarea',
          required: true,
          validation: [
            { type: 'required', message: 'U.S. address is required' }
          ],
          placeholder: 'Enter your U.S. address for tax purposes',
          conditional: {
            dependsOn: 'Q9',
            showWhen: 'Yes'
          }
        },
        {
          id: 'Q11',
          question: 'Do you have an Identity Protection PIN (IP PIN) from the IRS?',
          type: 'radio',
          required: true,
          options: ['Yes', 'No']
        },
        {
          id: 'Q12',
          question: 'What is your IP PIN?',
          type: 'text',
          required: true,
          validation: [
            { type: 'required', message: 'IP PIN is required' },
            { type: 'pattern', value: /^\d{6}$/, message: 'IP PIN must be 6 digits' }
          ],
          placeholder: '123456',
          conditional: {
            dependsOn: 'Q11',
            showWhen: 'Yes'
          }
        }
      ]
    }
    // Additional sections would be added here...
    // For brevity, I'm showing the structure. The full implementation would include all 15 sections
  ];
};

// Export the loader function
export const loadQuestionnaire = loadEnhancedQuestionnaire;

// Export helper to get all questions from all sections
export const getAllQuestions = (sections: Section[]): Question[] => {
  return sections.flatMap(section => section.questions);
};

// Export helper to get question by ID
export const getQuestionById = (questionId: string, sections: Section[]): Question | undefined => {
  return getAllQuestions(sections).find(q => q.id === questionId);
};

// Export helper to check if question should be shown based on answers
export const shouldShowQuestion = (
  question: Question, 
  answers: Record<string, any>,
  sectionIndex?: number,
  questionIndex?: number
): boolean => {
  // Always show the first question in the first section (Q1 should always be visible)
  if (sectionIndex === 0 && questionIndex === 0) {
    return true;
  }
  
  if (!question.conditional) return true;
  
  const { dependsOn, showWhen, hideWhen } = question.conditional;
  const dependentAnswer = answers[dependsOn];
  
  // Handle numeric comparisons FIRST (before showWhen/hideWhen checks)
  // This allows numeric conditions to work even when showWhen is undefined
  if (question.conditional?.conditionType === 'numeric' && question.conditional?.minValue !== undefined) {
    if (dependentAnswer === undefined || dependentAnswer === null || dependentAnswer === '') {
      // No answer yet - don't show if numeric condition is required
      // BUT: if this is the first question in a section, show it anyway to allow progression
      if (questionIndex === 0) {
        return true;
      }
      return false;
    }
    const numValue = typeof dependentAnswer === 'string' ? parseFloat(dependentAnswer) : Number(dependentAnswer);
    if (isNaN(numValue)) {
      return false;
    }
    return numValue >= question.conditional.minValue;
  }
  
  // If the dependency answer is undefined/null/empty, we need to decide:
  // - If hideWhen is specified and we don't have an answer, don't hide (show it)
  // - If showWhen is specified and we don't have an answer, don't show it (wait for answer)
  // However, if this is the first question in a section, we might want to show it anyway
  // For now, we'll be conservative: if showWhen is specified and answer is missing, hide it
  // But if hideWhen is specified and answer is missing, show it (don't hide)
  
  if (hideWhen !== undefined) {
    // Only hide if we have an answer AND it matches hideWhen
    if (dependentAnswer !== undefined && dependentAnswer !== null && dependentAnswer !== '') {
      if (dependentAnswer === hideWhen) {
        return false;
      }
    }
    // If no answer yet, don't hide (show the question)
  }
  
  if (showWhen !== undefined) {
    // Only show if we have an answer AND it matches showWhen
    if (dependentAnswer === undefined || dependentAnswer === null || dependentAnswer === '') {
      // No answer yet - don't show if showWhen is required
      // BUT: if this is the first question in a section, show it anyway to allow progression
      if (questionIndex === 0) {
        return true;
      }
      return false;
    }
    
    // Handle empty arrays (checkbox with nothing selected)
    if (Array.isArray(dependentAnswer) && dependentAnswer.length === 0) {
      if (questionIndex === 0) {
        return true;
      }
      return false;
    }
    
    // Handle checkbox questions where the answer is an array
    // Show if ANY of the selected values matches showWhen
    if (Array.isArray(dependentAnswer)) {
      if (Array.isArray(showWhen)) {
        // Both are arrays - check if any intersection
        return showWhen.some(sw => dependentAnswer.includes(sw));
      }
      // showWhen is a string, check if it's in the answer array
      return dependentAnswer.includes(showWhen);
    }
    
    // Handle when showWhen is an array but answer is a single value
    if (Array.isArray(showWhen)) {
      return showWhen.includes(dependentAnswer);
    }
    
    // Both are single values
    return dependentAnswer === showWhen;
  }
  
  return true;
};

// Export helper to get visible questions for a section
export const getVisibleQuestions = (
  section: Section, 
  answers: Record<string, any>,
  sectionIndex?: number
): Question[] => {
  return section.questions.filter((question, questionIndex) => 
    shouldShowQuestion(question, answers, sectionIndex, questionIndex)
  );
};

// Export helper to get next question with branching logic
export const getNextQuestion = (
  currentQuestionId: string, 
  sections: Section[], 
  answers: Record<string, any>
): { question: Question; sectionIndex: number; questionIndex: number } | null => {
  const allQuestions = getAllQuestions(sections);
  const currentIndex = allQuestions.findIndex(q => q.id === currentQuestionId);
  
  if (currentIndex === -1) return null;
  
  // Start from the next question and find the first visible one
  for (let i = currentIndex + 1; i < allQuestions.length; i++) {
    const question = allQuestions[i];
    
    // Find section index for this question
    let questionSectionIndex = -1;
    let questionIndexInSection = -1;
    for (let sIdx = 0; sIdx < sections.length; sIdx++) {
      const qIdx = sections[sIdx].questions.findIndex(q => q.id === question.id);
      if (qIdx !== -1) {
        questionSectionIndex = sIdx;
        questionIndexInSection = qIdx;
        break;
      }
    }
    
    if (shouldShowQuestion(question, answers, questionSectionIndex, questionIndexInSection)) {
      // Find which section this question belongs to
      for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
        const questionIndex = sections[sectionIndex].questions.findIndex(q => q.id === question.id);
        if (questionIndex !== -1) {
          return { question, sectionIndex, questionIndex };
        }
      }
    }
  }
  
  return null;
};

// Export helper to get previous question (only visible questions)
export const getPreviousQuestion = (
  currentQuestionId: string, 
  sections: Section[],
  answers: Record<string, any>
): { question: Question; sectionIndex: number; questionIndex: number } | null => {
  const allQuestions = getAllQuestions(sections);
  const currentIndex = allQuestions.findIndex(q => q.id === currentQuestionId);
  
  if (currentIndex <= 0) return null;
  
  // Start from the previous question and find the last visible one
  // (going backwards through the array)
  for (let i = currentIndex - 1; i >= 0; i--) {
    const question = allQuestions[i];
    
    // Find section index for this question
    let questionSectionIndex = -1;
    let questionIndexInSection = -1;
    for (let sIdx = 0; sIdx < sections.length; sIdx++) {
      const qIdx = sections[sIdx].questions.findIndex(q => q.id === question.id);
      if (qIdx !== -1) {
        questionSectionIndex = sIdx;
        questionIndexInSection = qIdx;
        break;
      }
    }
    
    // Only return if this question is visible
    if (shouldShowQuestion(question, answers, questionSectionIndex, questionIndexInSection)) {
      // Find which section this question belongs to
      for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
        const questionIndex = sections[sectionIndex].questions.findIndex(q => q.id === question.id);
        if (questionIndex !== -1) {
          return { question, sectionIndex, questionIndex };
        }
      }
    }
  }
  
  return null;
};

